/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * Controller for the lab management page.
 */
angular.module('nsadmin').controller('labManagementController', ['$scope', '$injector',
    function labManagementController($scope, $injector) {

        var labService = $injector.get('labService');
        var $location = $injector.get('$location');

        /**
         * All labs in the system.
         */
        $scope.labs = [];

        /**
         * New lab form data.
         */
        $scope.newLab = {
            lab_id: '',
            name: '',
            description: ''
        };

        /**
         * Device assignment form data.
         */
        $scope.deviceAssignment = {
            lab_id: '',
            device_slot: '',
            is_shared: false
        };

        /**
         * Lab device requirements.
         */
        $scope.labRequirements = [];

        /**
         * Available device slots.
         */
        $scope.deviceSlots = {
            pod: [],
            shared: []
        };

        /**
         * Loading states.
         */
        $scope.loading = {
            labs: true,
            requirements: false,
            deviceSlots: false
        };

        /**
         * Error messages.
         */
        $scope.errors = {
            labs: null,
            addLab: null,
            updateLab: null,
            deleteLab: null,
            deviceAssignment: null
        };

        /**
         * Success messages.
         */
        $scope.successMessage = null;

        /**
         * Loads all labs from the API.
         */
        function loadLabs() {
            $scope.loading.labs = true;
            $scope.errors.labs = null;

            labService.getAllLabs()
                .then(function success(labs) {
                    $scope.labs = labs.map(function(lab) {
                        lab.editing = false;
                        lab.originalData = angular.copy(lab);
                        return lab;
                    });
                    $scope.loading.labs = false;
                })
                ['catch'](function error(err) {
                    $scope.errors.labs = err.message || 'Failed to load labs';
                    $scope.loading.labs = false;
                    console.error('Error loading labs:', err);
                });
        }

        /**
         * Loads lab requirements (device assignments).
         */
        function loadLabRequirements() {
            $scope.loading.requirements = true;

            labService.getLabRequirements()
                .then(function success(requirements) {
                    $scope.labRequirements = requirements;
                    $scope.loading.requirements = false;
                })
                ['catch'](function error(err) {
                    console.error('Error loading lab requirements:', err);
                    $scope.loading.requirements = false;
                });
        }

        /**
         * Loads available device slots.
         */
        function loadDeviceSlots() {
            $scope.loading.deviceSlots = true;

            labService.getAvailableDeviceSlots()
                .then(function success(slots) {
                    $scope.deviceSlots = slots;
                    $scope.loading.deviceSlots = false;
                })
                ['catch'](function error(err) {
                    console.error('Error loading device slots:', err);
                    $scope.loading.deviceSlots = false;
                });
        }

        /**
         * Adds a new lab.
         */
        $scope.addLab = function addLab() {
            $scope.errors.addLab = null;
            $scope.successMessage = null;

            // Validate input
            if (!$scope.newLab.lab_id || !$scope.newLab.name) {
                $scope.errors.addLab = 'Lab ID and Name are required';
                return;
            }

            labService.createLab($scope.newLab)
                .then(function success() {
                    $scope.successMessage = 'Lab added successfully';
                    $scope.newLab = {
                        lab_id: '',
                        name: '',
                        description: ''
                    };
                    loadLabs();
                })
                ['catch'](function error(err) {
                    $scope.errors.addLab = err.message || 'Failed to add lab';
                    console.error('Error adding lab:', err);
                });
        };

        /**
         * Starts editing a lab.
         *
         * @param {Object} lab
         *     The lab to edit.
         */
        $scope.editLab = function editLab(lab) {
            // Cancel any other edits
            $scope.labs.forEach(function(l) {
                if (l !== lab && l.editing) {
                    $scope.cancelEdit(l);
                }
            });

            lab.editing = true;
            lab.originalData = angular.copy(lab);
        };

        /**
         * Saves changes to a lab.
         *
         * @param {Object} lab
         *     The lab to save.
         */
        $scope.saveLab = function saveLab(lab) {
            $scope.errors.updateLab = null;
            $scope.successMessage = null;

            labService.updateLab(lab.lab_id, {
                name: lab.name,
                description: lab.description
            })
            .then(function success() {
                $scope.successMessage = 'Lab updated successfully';
                lab.editing = false;
                lab.originalData = angular.copy(lab);
            })
            ['catch'](function error(err) {
                $scope.errors.updateLab = err.message || 'Failed to update lab';
                console.error('Error updating lab:', err);
            });
        };

        /**
         * Cancels editing a lab.
         *
         * @param {Object} lab
         *     The lab to cancel editing.
         */
        $scope.cancelEdit = function cancelEdit(lab) {
            lab.name = lab.originalData.name;
            lab.description = lab.originalData.description;
            lab.editing = false;
        };

        /**
         * Deletes a lab.
         *
         * @param {Object} lab
         *     The lab to delete.
         */
        $scope.deleteLab = function deleteLab(lab) {
            $scope.errors.deleteLab = null;
            $scope.successMessage = null;

            if (!confirm('Are you sure you want to delete lab "' + lab.name + '"? This action cannot be undone.')) {
                return;
            }

            labService.deleteLab(lab.lab_id)
                .then(function success() {
                    $scope.successMessage = 'Lab deleted successfully';
                    loadLabs();
                    loadLabRequirements(); // Refresh requirements as well
                })
                ['catch'](function error(err) {
                    $scope.errors.deleteLab = err.message || 'Failed to delete lab';
                    console.error('Error deleting lab:', err);
                });
        };

        /**
         * Assigns a device to a lab.
         */
        $scope.assignDevice = function assignDevice() {
            $scope.errors.deviceAssignment = null;
            $scope.successMessage = null;

            // Validate input
            if (!$scope.deviceAssignment.lab_id || !$scope.deviceAssignment.device_slot) {
                $scope.errors.deviceAssignment = 'Lab and Device Slot are required';
                return;
            }

            labService.addDeviceToLab($scope.deviceAssignment.lab_id, {
                device_slot: $scope.deviceAssignment.device_slot,
                is_shared: $scope.deviceAssignment.is_shared
            })
            .then(function success() {
                $scope.successMessage = 'Device assigned to lab successfully';
                $scope.deviceAssignment = {
                    lab_id: '',
                    device_slot: '',
                    is_shared: false
                };
                loadLabRequirements();
            })
            ['catch'](function error(err) {
                $scope.errors.deviceAssignment = err.message || 'Failed to assign device';
                console.error('Error assigning device:', err);
            });
        };

        /**
         * Removes a device from a lab.
         *
         * @param {Object} requirement
         *     The lab requirement to remove.
         */
        $scope.removeDeviceFromLab = function removeDeviceFromLab(requirement) {
            $scope.errors.deviceAssignment = null;
            $scope.successMessage = null;

            if (!confirm('Remove device ' + requirement.device_slot + ' from lab ' + requirement.lab_name + '?')) {
                return;
            }

            labService.removeDeviceFromLab(requirement.lab_id, requirement.device_slot, requirement.is_shared)
                .then(function success() {
                    $scope.successMessage = 'Device removed from lab successfully';
                    loadLabRequirements();
                })
                ['catch'](function error(err) {
                    $scope.errors.deviceAssignment = err.message || 'Failed to remove device';
                    console.error('Error removing device:', err);
                });
        };

        /**
         * Clears success/error messages.
         */
        $scope.clearMessages = function clearMessages() {
            $scope.successMessage = null;
            $scope.errors = {
                labs: null,
                addLab: null,
                updateLab: null,
                deleteLab: null,
                deviceAssignment: null
            };
        };

        /**
         * Navigates back to the dashboard.
         */
        $scope.goToDashboard = function goToDashboard() {
            $location.path('/nsadmin/dashboard');
        };

        // Load data on controller initialization
        loadLabs();
        loadLabRequirements();
        loadDeviceSlots();

        console.log('Lab Management controller loaded');
    }
]);
